import React from "react";
import { Sparkles, CheckCircle2, Star, Users } from "lucide-react";
import { resolveSessionKind } from "../sessionKind";
import "../demo-classes.scss";

/**
 * The public chrome for BOTH live-session products. `kind` picks the words;
 * everything structural — the split hero, the mobile bar, the centered card —
 * is identical, which is why a webinar did not need a second shell.
 */

// Curated free Unsplash image -- collaborative learners (license-free).
const HERO_IMAGE_URL =
  "https://images.unsplash.com/photo-1543269865-cbf427effbad?auto=format&fit=crop&w=1600&q=80";

const DemoPublicShell = ({
  institute,
  children,
  wide = false,
  split = false,
  hero,
  footer,
  kind = "demo",
}) => {
  const vocab = resolveSessionKind(kind);
  const name = institute?.name || vocab.FullNoun;
  const logo = institute?.logoUrl;
  const brandStyle = institute?.primaryColor
    ? { "--brand-primary": institute.primaryColor }
    : undefined;

  if (split) {
    const heroTitle = hero?.title || vocab.copy.heroTitle;
    const heroSubtitle = hero?.subtitle || vocab.copy.heroSubtitle;
    const features = hero?.features || vocab.copy.heroFeatures;
    const trust = hero?.trust || {
      stat: "10,000+ learners",
      rating: "4.9 / 5 average rating",
      quote: `The ${vocab.noun} helped me decide in 30 minutes -- totally worth it.`,
      author: "-- Recent attendee",
    };

    return (
      <div className="demo-public demo-public--split" style={brandStyle}>
        <main className="demo-public__main">
          <div className="demo-split">
            <aside className="demo-split__hero">
              <div
                className="demo-split__hero-img"
                style={{ backgroundImage: `url(${HERO_IMAGE_URL})` }}
              />
              <div className="demo-split__hero-overlay" />
              <div className="demo-split__hero-pattern" />
              <span className="demo-split__hero-blob demo-split__hero-blob--a" />
              <span className="demo-split__hero-blob demo-split__hero-blob--b" />

              <div className="demo-split__hero-top">
                <span className="demo-split__hero-tag">
                  <Sparkles size={14} /> {vocab.copy.heroTag}
                </span>
                <h1 className="demo-split__hero-title">{heroTitle}</h1>
                <p className="demo-split__hero-sub">{heroSubtitle}</p>
                <ul className="demo-split__hero-features">
                  {features.map((f) => (
                    <li key={f}>
                      <CheckCircle2 size={16} /> {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="demo-split__hero-trust">
                <div className="demo-split__hero-trust-row">
                  <Users size={16} /> <strong>{trust.stat}</strong> have joined {vocab.noun === "webinar" ? "webinars" : "demos"}
                </div>
                <div className="demo-split__hero-trust-row">
                  <Star size={16} className="demo-split__hero-star" /> <strong>{trust.rating}</strong>
                </div>
                {trust.quote && (
                  <p className="demo-split__hero-trust-quote">
                    "{trust.quote}"
                    {trust.author && (
                      <span className="demo-split__hero-trust-author">{trust.author}</span>
                    )}
                  </p>
                )}
              </div>
            </aside>

            <section className="demo-split__panel">
              {/* Mobile-only top bar — visible only when hero is hidden */}
              <div className="demo-split__mobile-bar">
                <div className="demo-split__mobile-bar-brand">
                  {logo
                    ? <img src={logo} alt={name} className="demo-split__mobile-bar-logo" />
                    : <span className="demo-split__mobile-bar-name">{name}</span>
                  }
                </div>
                <span className="demo-split__mobile-bar-badge">
                  <Sparkles size={12} /> {vocab.copy.heroTag}
                </span>
              </div>

              <div className="demo-split__body">
                <div className="demo-split__inner">
                  {/* Institute row hidden on mobile — shown in mobile-bar above */}
                  <div className="demo-split__institute">
                    {logo ? (
                      <img src={logo} alt={name} />
                    ) : (
                      <div
                        className="demo-split__institute-text"
                        style={{ fontSize: "1.15rem", fontWeight: 500 }}
                      >
                        {name}
                      </div>
                    )}
                    {logo && (
                      <div className="demo-split__institute-text">
                        <span className="demo-split__institute-name">{name}</span>
                        <span className="demo-split__institute-label">
                          {vocab.copy.hostedLabel}
                        </span>
                      </div>
                    )}
                  </div>

                  {children}
                </div>
              </div>

              {footer && (
                <div className="demo-split__footer">
                  <div className="demo-split__footer-inner">{footer}</div>
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    );
  }

  // Legacy centered card (kept for lobby / live / thank-you pages).
  return (
    <div className="demo-public" style={brandStyle}>
      <header className="demo-public__bar">
        <div className="demo-public__brand">
          {logo ? <img src={logo} alt={name} /> : null}
          {!logo ? <span>{name}</span> : null}
        </div>
        <span className="demo-public__bar-label">{vocab.copy.shellLabel}</span>
      </header>
      <main className="demo-public__main">
        <div className={`demo-public__card ${wide ? "demo-public__card--wide" : ""}`}>
          {children}
        </div>
      </main>
      <footer className="demo-public__footer">
        Powered by LMS Campus
      </footer>
    </div>
  );
};

export default DemoPublicShell;
